
import React from "react";
import { useTranslation } from 'react-i18next';
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { AtSign, Phone, MapPin } from "lucide-react";
import { LinkedinIcon, XiaohongshuIcon, BilibiliIcon, YoutubeIcon, InstagramIcon } from "@/components/SocialMediaIcons";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FAQStructuredData } from "@/components/seo/FAQStructuredData";

const faqCategories = {
  aboutPlatform: ['howItWorks', 'practitionersVerified', 'serviceScope', 'clinicalVsNonClinical', 'aiDisclaimer', 'emergencyCrisis'],
  bookingSessions: ['bookAppointment', 'bookingRules', 'appointmentChanges', 'serviceChanges', 'sessionFormats', 'choosePractitioner'],
  paymentPricing: ['pricing', 'currencyPolicy', 'paymentSecurity', 'platformCommission'],
  cancellationRefunds: ['cancelAppointment', 'refundProcess', 'disputeResolution', 'contactSupport'],
  insurance: ['insuranceReimbursement'],
  other: ['healingStages', 'privacy', 'techRequirements'],
};

const allFaqKeys = Object.values(faqCategories).flat();

const AboutUs = () => {
  const { t } = useTranslation('about');

  // FAQ items for structured data (all items regardless of tab)
  const faqItems = allFaqKeys.map((key) => ({
    question: t(`faqItems.${key}.question`),
    answer: t(`faqItems.${key}.answer`),
  }));

  const renderFaqItems = (keys: string[], startIndex: number) => (
    keys.map((key, i) => (
      <AccordionItem key={key} value={`item-${startIndex + i}`} className="border-none">
        <AccordionTrigger className="px-6 py-4 text-xl font-medium text-earth-800 hover:no-underline hover:text-brand-700 text-left">
          {t(`faqItems.${key}.question`)}
        </AccordionTrigger>
        <AccordionContent className="px-6 pb-4 text-earth-700">
          {t(`faqItems.${key}.answer`)}
        </AccordionContent>
      </AccordionItem>
    ))
  );

  return (
    <div className="min-h-screen flex flex-col">
      <FAQStructuredData faqItems={faqItems} />
      <Navbar />
      <main className="flex-grow bg-warm-50 py-24 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Company Vision Section */}
          <section className="mb-16">
            <h1 className="text-3xl md:text-4xl font-serif font-medium text-earth-900 mb-6 text-center">
              {t('ourVision')}
            </h1>
              <div className="bg-white p-8 rounded-lg shadow-sm border border-warm-100">
              <p className="text-lg text-earth-700 leading-relaxed mb-6">
                {t('visionDescription1')}
              </p>
              <p className="text-lg text-earth-700 leading-relaxed">
                {t('visionDescription2')}
              </p>
            </div>
          </section>

          {/* Q&A Section with Tabs + Accordion */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-serif font-medium text-earth-900 mb-6 text-center">
              {t('faq')}
            </h2>
            <Tabs defaultValue="aboutPlatform" className="w-full">
              <TabsList className="w-full flex flex-wrap h-auto gap-1 bg-warm-100 p-1 mb-4 rounded-lg">
                {Object.keys(faqCategories).map((catKey) => (
                  <TabsTrigger
                    key={catKey}
                    value={catKey}
                    className="text-sm data-[state=active]:bg-white data-[state=active]:text-brand-700 data-[state=active]:shadow-sm"
                  >
                    {t(`faqCategories.${catKey}`)}
                  </TabsTrigger>
                ))}
              </TabsList>
              {Object.entries(faqCategories).map(([catKey, keys]) => (
                <TabsContent key={catKey} value={catKey}>
                  <div className="bg-white rounded-lg shadow-sm border border-warm-100">
                    <Accordion type="single" collapsible className="divide-y divide-warm-100">
                      {renderFaqItems(keys, 0)}
                    </Accordion>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </section>

          {/* Contact Us Section */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-serif font-medium text-earth-900 mb-6 text-center">
              {t('contactUs')}
            </h2>
              <div className="bg-white p-8 rounded-lg shadow-sm border border-warm-100">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-start">
                    <MapPin className="h-5 w-5 text-brand-600 mr-3 mt-1" />
                    <div>
                      <p className="font-medium">{t('address')}</p>
                      <p className="text-earth-700">425 Washington Blvd, Jersey City, NJ 07310</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <Phone className="h-5 w-5 text-brand-600 mr-3 mt-1" />
                    <div>
                      <p className="font-medium">{t('phone')}</p>
                      <p className="text-earth-700">1-909-905-7788</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <AtSign className="h-5 w-5 text-brand-600 mr-3 mt-1" />
                    <div>
                      <p className="font-medium">{t('email')}</p>
                      <p className="text-earth-700">info@resonow.org</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <p className="font-medium mb-2">{t('connectWithUs')}</p>
                  <div className="flex space-x-6">
                    <a 
                      href="http://linkedin.com/company/resonow" 
                      className="hover:opacity-80 transition-opacity"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <LinkedinIcon size={32} />
                    </a>
                    <a 
                      href="https://www.xiaohongshu.com/user/profile/63e3d3dd0000000027028c96?xsec_token=YB69VkF1ScEtyl3APnzi4mfU_wUdQD2J6tJCMmyCbIroI=&xsec_source=app_share&xhsshare=CopyLink&appuid=63e3d3dd0000000027028c96&apptime=1748829819&share_id=4b83f0e9d7254de5930d6cd2ce16c2d6"
                      className="hover:opacity-80 transition-opacity"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <XiaohongshuIcon size={32} />
                    </a>
                    <a href="#" className="hover:opacity-80 transition-opacity">
                      <BilibiliIcon size={32} />
                    </a>
                    <a 
                      href="https://www.youtube.com/channel/UC33iC81J5UUfMiaNSiReu8w" 
                      className="hover:opacity-80 transition-opacity"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <YoutubeIcon size={32} />
                    </a>
                    <a 
                      href="https://www.instagram.com/resonow.official?igsh=aTFlOW90cjZxaHV3" 
                      className="hover:opacity-80 transition-opacity"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <InstagramIcon size={32} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Join Our Team Section */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-serif font-medium text-earth-900 mb-6 text-center">
              {t('joinTeam.title')}
            </h2>
            <div className="bg-white p-8 rounded-lg shadow-sm border border-warm-100">
              <p className="text-lg text-earth-700 leading-relaxed mb-6 text-center">
                {t('joinTeam.description')}
              </p>
              <div className="text-center">
                <Button asChild size="lg" className="bg-brand-500 hover:bg-brand-600">
                  <Link to="/careers">{t('joinTeam.viewPositions')}</Link>
                </Button>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AboutUs;
